/**
 * ADM Sport — autocomplétion de la recherche
 * ============================================================================
 * ÉCRIT À LA MAIN. NE PAS COMPILER. NE PAS PASSER PAR WEBPACK.
 *
 * Ce fichier remplace assets/component.predictive-search.js, qui était un
 * bundle webpack produit par la chaîne de build de Molsoft. Cette chaîne
 * dépend de deux paquets privés (@molsoft-io/molsoft-tools et
 * @molsoft-io/multilingual-module) hébergés sur le registre GitHub de
 * Molsoft — donc inaccessibles depuis la fin du contrat.
 *
 * D'où le choix du JavaScript natif : custom elements, fetch, URLSearchParams
 * et AbortController sont supportés par tous les navigateurs visés. Aucune
 * transpilation, aucune dépendance, aucun build. On dépose, ça marche.
 *
 * Le nom volontairement hors convention Molsoft (adm-*) signale que ce
 * fichier est maintenu à la main et ne doit pas être régénéré.
 *
 * Remplace : scripts/optimized_components/predictive-search.js (2024)
 * Voir     : guide-implementation-autocompletion.md, étape 4
 * Version  : 1.0 — 28 juillet 2026
 * ============================================================================
 */
(function () {
  'use strict';

  // --- Réglages -------------------------------------------------------------

  // 2 et non 3 : « V1 » (82 sessions/3 mois), « R7 » (64), « K3 » (62),
  // « C5 » (45), « S3 » (41) sont des modèles produits réels et très cherchés.
  var MIN_CHARS = 2;

  // 220 ms. L'ancien code était à 500 ms — perçu comme lent. En dessous de
  // 150 ms on multiplie les appels sans que l'utilisateur voie la différence.
  var DEBOUNCE_MS = 220;

  // 10 = plafond dur de Shopify. Attention, c'est un budget TOTAL réparti
  // entre les types demandés : avec product+collection on obtient 5 + 5.
  // Au-delà de 10, la valeur est ramenée à 10 en silence (statut 200).
  var LIMIT = 10;

  // Les champs interrogés. variants.sku et variants.barcode sont le seul
  // levier possible sur les 27 % de recherches contenant un chiffre : la doc
  // Shopify précise que les synonymes ne s'appliquent JAMAIS à ces champs.
  var FIELDS = 'title,product_type,vendor,variants.sku,variants.barcode';

  // --- Normalisation de la requête -----------------------------------------

  /**
   * 29,24 % des sessions de recherche arrivent sur une graphie que le
   * catalogue ne reconnaît pas. Ces quatre règles en récupèrent l'essentiel,
   * côté client, sans rien attendre de Shopify.
   *
   * Ce qu'on ne touche PAS : la casse et les accents. Shopify s'en occupe
   * déjà (« chaine » 295 résultats vs « chain » 263 ; « botte » 733 vs
   * « bottes » 748), et les retirer dégraderait l'affichage à l'écran.
   */
  function normalizeQuery(raw) {
    return String(raw)
      .normalize('NFC')
      // Apostrophes typographiques → droite. 26 termes / 219 sessions.
      // « Rev’it » (41 sessions) ne matche pas l'apostrophe du catalogue.
      .replace(/[‘’ʼ]/g, "'")
      // Tabulations : 4 termes, collages depuis un tableur de pièces
      // (« 01-830913\t »).
      .replace(/\t/g, ' ')
      // Espaces multiples : 13 termes (« Sac a  dos », « Gant  moto »).
      .replace(/\s+/g, ' ')
      // Espaces de tête et de queue : 1 179 termes / 11 914 sessions
      // (11,89 %). « Casque » avec espace final = 587 sessions à lui seul.
      .trim();
  }

  // --- URL multilingue ------------------------------------------------------

  /**
   * Reprise de scripts/utils/multilingual-url.js, recopiée ici pour couper
   * la dépendance au build. Les deux globales lues (multilingual_settings et
   * i18next) sont posées par la section Liquid indirect-translations.liquid,
   * donc disponibles à l'exécution indépendamment de toute compilation.
   *
   * Ne JAMAIS coder /search/suggest en dur : la version anglaise cesserait
   * de fonctionner.
   */
  function multilingualUrl(path) {
    try {
      // Le prefixe vient de Shopify (Shopify.routes.root), pas de i18next.
      // i18next devine la langue depuis l'URL ; or la locale par defaut du
      // domaine (le francais) n'a AUCUN prefixe dans l'URL, donc il se trompe
      // et renvoie "en" sur tout le site francais.
      // Shopify.routes.root vaut "/" en francais et "/en/" sous /en : c'est
      // Shopify qui a rendu la page, il ne peut pas se tromper.
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var prefix = root.replace(/\/$/, '');
      if (!prefix) return path;
      return prefix + path;
    } catch (e) {
      return path; // en cas de doute, l'URL sans préfixe vaut mieux que rien
    }
  }

  // --- Composant ------------------------------------------------------------

  class AdmPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return; // évite la double initialisation
      this._ready = true;

      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.form = this.querySelector('[data-search-form]');
      this.status = this.querySelector('[data-search-status]');

      if (!this.input || !this.results) {
        // Sans ces deux éléments, rien n'est possible. On le dit clairement
        // plutôt que de laisser un TypeError obscur — c'est exactement le
        // piège dans lequel tombait l'ancien composant.
        console.warn(
          '[adm-predictive-search] Balisage incomplet : ' +
            (!this.input ? '[data-search-input] ' : '') +
            (!this.results ? '[data-search-results]' : '') +
            ' introuvable. Voir le guide, étape 2.'
        );
        return;
      }

      this.timer = null;
      this.controller = null;
      this.options = [];
      this.activeIndex = -1;
      this.lastQuery = '';

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this), true);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
    }

    // --- Saisie -------------------------------------------------------------

    onInput() {
      var q = normalizeQuery(this.input.value);
      clearTimeout(this.timer);

      if (q.length < MIN_CHARS) {
        this.close();
        return;
      }

      // Rien de nouveau à demander : évite un aller-retour quand
      // l'utilisateur ajoute puis retire un espace.
      if (q === this.lastQuery && this.options.length) {
        this.open();
        return;
      }

      this.timer = setTimeout(this.fetchResults.bind(this, q), DEBOUNCE_MS);
    }

    // --- Requête ------------------------------------------------------------

    fetchResults(q) {
      var self = this;

      // Annule la requête précédente. Sans ça les réponses arrivent dans le
      // désordre : le résultat de « cas » peut s'afficher après celui de
      // « casque ». Panneau qui clignote et résultats faux.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var params = new URLSearchParams();
      // IMPORTANT : q brut, sans « * » ajouté. Voir la note en bas de fichier.
      params.set('q', q);
      params.set('resources[type]', 'product,collection');
      params.set('resources[limit]', String(LIMIT));
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', FIELDS);
      params.set('section_id', 'predictive-search');

      var url = multilingualUrl('/search/suggest') + '?' + params.toString();

      this.results.classList.add('loading');

      fetch(url, { signal: this.controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('#shopify-section-predictive-search');

          self.results.innerHTML = section ? section.innerHTML : '';
          self.lastQuery = q;
          self.open();
        })
        .catch(function (err) {
          // Une annulation n'est pas une erreur : c'est le comportement voulu.
          if (err.name === 'AbortError') return;
          console.warn('[adm-predictive-search]', err);
          self.close();
        })
        .then(function () {
          self.results.classList.remove('loading');
        });
    }

    // --- Ouverture et fermeture --------------------------------------------

    open() {
      this.options = Array.prototype.slice.call(
        this.results.querySelectorAll('[role="option"]')
      );

      if (!this.options.length) {
        // Le panneau reste visible : il contient le message « aucun résultat ».
        this.input.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.announce(0);
        return;
      }

      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.setActive(-1);
      this.announce(this.options.length);
    }

    close() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();

      this.results.innerHTML = '';
      this.options = [];
      this.activeIndex = -1;
      this.lastQuery = '';

      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
    }

    /**
     * Annonce le nombre de suggestions aux lecteurs d'écran. Sans ça, un
     * utilisateur non voyant ne sait pas que quelque chose est apparu.
     */
    announce(count) {
      if (!this.status) return;
      this.status.textContent =
        count === 0
          ? 'Aucune suggestion'
          : count + (count > 1 ? ' suggestions' : ' suggestion');
    }

    // --- Navigation clavier -------------------------------------------------

    setActive(index) {
      this.options.forEach(function (el) {
        el.classList.remove('is-active');
        el.setAttribute('aria-selected', 'false');
      });

      this.activeIndex = index;

      if (index < 0 || !this.options[index]) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      var el = this.options[index];
      el.classList.add('is-active');
      el.setAttribute('aria-selected', 'true');
      if (el.id) this.input.setAttribute('aria-activedescendant', el.id);

      // Garde l'option visible si la liste défile.
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    /**
     * Déplace la sélection en boucle sur n+1 positions : les n options, plus
     * la position -1 qui correspond au champ de saisie lui-même. Ça permet de
     * revenir au texte tapé en remontant, comportement attendu d'un combobox.
     */
    move(delta) {
      var n = this.options.length;
      if (!n) return;

      // Décalage de 1 pour travailler sur 0..n, puis retour à -1..n-1.
      var pos = (this.activeIndex + 1 + delta + (n + 1)) % (n + 1);
      this.setActive(pos - 1);
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.move(1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.move(-1);
          break;

        case 'Enter':
          // Une option sélectionnée : on suit son lien. Sinon on laisse le
          // formulaire se soumettre normalement vers /search.
          if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
            var link = this.options[this.activeIndex].querySelector('a');
            if (link) {
              event.preventDefault();
              window.location.href = link.href;
            }
          }
          break;

        case 'Escape':
          // Ferme le panneau mais garde le texte saisi.
          this.close();
          this.input.focus();
          break;

        case 'Tab':
          this.close();
          break;
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }
  }

  if (!window.customElements.get('adm-predictive-search')) {
    window.customElements.define('adm-predictive-search', AdmPredictiveSearch);
  }
})();

/**
 * ============================================================================
 * NOTE — POURQUOI IL N'Y A PAS DE « * » DANS LA REQUÊTE
 * ============================================================================
 *
 * L'ancien composant Molsoft construisait sa requête ainsi :
 *
 *     q: `${searchInputValue}* ${currentInputFilters.value}`
 *
 * Il ne faut PAS reprendre ce comportement, pour trois raisons.
 *
 * 1. Le « * » est de la syntaxe de recherche Shopify. La documentation est
 *    explicite : les SYNONYMES ne sont pas utilisés quand la requête contient
 *    de la syntaxe de recherche, et les BOOSTS PRODUITS ne sont pas appliqués
 *    non plus. Autrement dit, garder le « * » rendrait inutile tout le
 *    travail de saisie des synonymes.
 *
 * 2. Mesuré sur la boutique le 28 juillet 2026 : « casq » retourne 40
 *    résultats, « casq* » en retourne 3 023. La pertinence s'effondre.
 *
 * 3. La bonne façon de faire de la correspondance par préfixe est le
 *    paramètre options[prefix]=last, déjà présent en champ caché dans le
 *    formulaire du header.
 *
 * Le « currentInputFilters.value » de l'ancien code est également supprimé :
 * il lisait [data-search-filter], un élément absent du DOM actuel, ce qui
 * levait un TypeError. Même problème avec closest('details') dans la branche
 * de champ vide — il n'y a plus de <details> autour de la recherche.
 * ============================================================================
 */
